// src/comps/AccountDropdown.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";

const AccountDropdown = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setLoading(false);
        return;
      }

      const res = await fetch(`${API_URL}/api/users/me`, {
        headers: {
          "x-auth-token": token,
        },
      });

      if (res.ok) {
        const data = await res.json();
        setUser(data);
      }
    } catch (err) {
      console.error("Error fetching user profile:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  const getMembershipTypeLabel = (role) => {
    switch (role) {
      case "general":
        return "General Member";
      case "registered":
        return "Registered Member";
      case "lifetime":
        return "Lifetime Member";
      default:
        return "Member";
    }
  };

  const getPaymentStatus = () => {
    if (!user) return null;

    // General members never pay
    if (user.role === "general") {
      return {
        message: "No payment required",
        color: "text-green-600",
        showReminder: false,
      };
    }

    // Lifetime members pay once
    if (user.role === "lifetime") {
      const hasPaid = user.paymentInfo?.lastPaymentDate;
      if (hasPaid) {
        return {
          message: "Lifetime membership active",
          color: "text-green-600",
          showReminder: false,
        };
      } else {
        return {
          message: "Payment pending",
          color: "text-red-600",
          showReminder: true,
        };
      }
    }

    // Registered members pay yearly
    if (user.role === "registered") {
      const nextDue = user.paymentInfo?.nextPaymentDue;
      if (!nextDue) {
        return {
          message: "Payment required",
          color: "text-red-600",
          showReminder: true,
        };
      }

      const dueDate = new Date(nextDue);
      const today = new Date();
      const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

      if (daysUntilDue < 0) {
        return {
          message: "Payment overdue",
          color: "text-red-600",
          showReminder: true,
        };
      } else if (daysUntilDue <= 30) {
        return {
          message: `Payment due in ${daysUntilDue} days`,
          color: "text-yellow-600",
          showReminder: true,
        };
      } else {
        return {
          message: `Next payment: ${dueDate.toLocaleDateString()}`,
          color: "text-green-600",
          showReminder: false,
        };
      }
    }

    return null;
  };

  if (loading) {
    return (
      <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 p-4">
        <p className="text-gray-500 text-center">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 p-4">
        <p className="text-gray-500 text-center">Unable to load profile</p>
        <button
          onClick={handleLogout}
          className="w-full mt-3 bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition"
        >
          Logout
        </button>
      </div>
    );
  }

  const paymentStatus = getPaymentStatus();

  return (
    <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50">
      {/* User Info Header */}
      <div className="bg-[#800000] text-white p-4">
        <h3 className="font-bold text-lg">
          {user.firstName} {user.lastName}
        </h3>
        <p className="text-sm opacity-90">{user.email}</p>
        <p className="text-sm mt-1 opacity-90">
          {getMembershipTypeLabel(user.role)}
        </p>
      </div>

      {/* Payment Status */}
      <div className="p-4 border-b border-gray-200">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">
          Membership Status
        </h4>
        {paymentStatus && (
          <div>
            <p className={`text-sm font-medium ${paymentStatus.color}`}>
              {paymentStatus.message}
            </p>
            {paymentStatus.showReminder && (
              <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded p-2">
                <p className="text-xs text-yellow-800">
                  Please ensure your membership fees are up to date. Contact us
                  if you have any questions.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Payment History (if exists) */}
      {user.paymentInfo?.paymentHistory?.length > 0 && (
        <div className="p-4 border-b border-gray-200 max-h-40 overflow-y-auto">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">
            Recent Payments
          </h4>
          {user.paymentInfo.paymentHistory.slice(-3).reverse().map((payment, idx) => (
            <div key={idx} className="text-xs text-gray-600 mb-1">
              <span className="font-medium">
                {new Date(payment.date).toLocaleDateString()}
              </span>
              {payment.amount && <span> - ${payment.amount}</span>}
              {payment.method && <span> ({payment.method})</span>}
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="p-4 space-y-2">
        <button
          onClick={() => navigate("/payment-confirmation")}
          className="w-full bg-[#800000] text-white py-2 px-4 rounded hover:bg-[#9b0000] transition"
        >
          Make Payment
        </button>
        <button
          onClick={handleLogout}
          className="w-full bg-gray-600 text-white py-2 px-4 rounded hover:bg-gray-700 transition"
        >
          Logout
        </button>
      </div>
    </div>
  );
};

export default AccountDropdown;
